import type {
  UnifiedBranchesData,
  ResolvedBranchesData,
  ResolvedBranchNode,
  ResolvedBranchNodeSection,
} from '@/lib/branchTypes'

// Mirror of lib/resolveBranchContent.ts but resolves markdown from an
// in-memory chunk map (the same one produced by splitMarkdownToBranches)
// instead of reading from disk. This is what the editor uses to render
// PlaybookBranchNode for the live preview without ever touching fs.
//
// The chunk map keys match the relative paths inside `content/` (the same
// strings stored on `node.markdownFile` / `section.markdownFile` /
// `node.footnotesFile`). For sections+footnotes already-loaded markdown
// embedded in the unified JSON would also work; this resolver always
// prefers the in-memory chunk map and falls back to empty string when a
// referenced file isn't present (typical when a chunk got renamed).
export function resolveBranchContentFromMemory({
  unifiedJson,
  chunkContents,
}: {
  unifiedJson: UnifiedBranchesData
  chunkContents: Record<string, string>
}): ResolvedBranchesData {
  const resolvedNodes: Record<string, ResolvedBranchNode> = {}

  for (const [nodeId, node] of Object.entries(unifiedJson.nodes)) {
    const markdown = node.markdownFile ? chunkContents[node.markdownFile] || '' : ''
    let resolvedSections: ResolvedBranchNodeSection[] | undefined
    if (node.sections) {
      resolvedSections = node.sections.map(section => {
        const sectionMarkdown = section.markdownFile ? chunkContents[section.markdownFile] || '' : ''
        return { ...section, content: { markdown: sectionMarkdown } }
      })
    }
    const footnotesMarkdown = node.footnotesFile
      ? chunkContents[node.footnotesFile] || undefined
      : undefined

    resolvedNodes[nodeId] = {
      ...node,
      content: { markdown },
      sections: resolvedSections,
      footnotesMarkdown,
    }
  }

  return {
    version: unifiedJson.version,
    rootId: unifiedJson.rootId,
    nodes: resolvedNodes,
    effectiveComputeByBranch: unifiedJson.effectiveComputeByBranch,
  }
}
